import { useNavigate, useParams } from 'react-router-dom';
import { Security } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { BundleAssignmentsSection } from '../components/bundle-assignments-section';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetBundleQuery } from '../store/hooks';

export default function BundleDetailPage() {
  const navigate = useNavigate();
  const { bundleId } = useParams<{ bundleId: string }>();

  const {
    data: bundle,
    isLoading,
    error,
  } = useGetBundleQuery({ bundleId: bundleId! }, { skip: !bundleId });

  const grants = bundle?.grants || [];

  if (!bundleId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid Bundle ID</h2>
            <Button onClick={() => navigate('/admin-v2/bundles')} className="mt-4">
              Back to Bundles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading bundle..." />
        </main>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading bundle</h2>
            <p className="text-gray-600 mt-2">Unable to fetch bundle details.</p>
            <Button onClick={() => navigate('/admin-v2/bundles')} className="mt-4">
              Back to Bundles
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/bundles" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Bundle Details</h1>
        </div>

        <div className="space-y-6">
          {/* Bundle Info Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4">
                Bundle Information
              </Typography>
              <div className="space-y-6">
                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Name
                  </Typography>
                  <Typography variant="h6">{bundle.name}</Typography>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {bundle.description || (
                      <span className="text-gray-400 italic">No description</span>
                    )}
                  </Typography>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Stripe Product ID
                  </Typography>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {bundle.stripe_product_id}
                  </code>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Bundle ID
                  </Typography>
                  <Typography variant="body2" className="font-mono text-gray-600">
                    {bundle.bundle_id}
                  </Typography>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(new Date(bundle.created_at))}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      {formatRelativeTime(new Date(bundle.created_at))}
                    </Typography>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(new Date(bundle.updated_at))}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      {formatRelativeTime(new Date(bundle.updated_at))}
                    </Typography>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capabilities Card */}
          <Card>
            <CardContent>
              <div className="mb-4">
                <Typography variant="h6">Granted Capabilities</Typography>
                <Typography variant="body2" className="text-gray-500">
                  Capabilities included in this bundle
                </Typography>
              </div>

              {grants.length === 0 ? (
                <div className="text-center py-8">
                  <Security className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <Typography variant="body1" className="text-gray-500">
                    No capabilities granted
                  </Typography>
                </div>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Capability</TableCell>
                      <TableCell>Metering</TableCell>
                      <TableCell>Quota</TableCell>
                      <TableCell>Period</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grants.map(grant => (
                      <TableRow key={grant.grant_id}>
                        <TableCell>
                          <div className="flex items-center">
                            <Security className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="font-medium">
                              {grant.capability_name || grant.capability_id}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={grant.metering_type}
                            size="small"
                            color={grant.metering_type === 'none' ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {grant.quota !== null ? grant.quota : (
                            <span className="text-gray-400">Unlimited</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {grant.usage_period || (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Assignments Card */}
          <BundleAssignmentsSection bundleId={bundleId} />
        </div>
      </main>
    </div>
  );
}
